import axios from 'axios';
import { settings } from '@/plugins/settings';

export const AUDIT_LOG_ACTION_INSERT = 'insert';
export const AUDIT_LOG_ACTION_UPDATE = 'update';
export const AUDIT_LOG_ACTION_DELETE = 'delete';

export const AUDIT_LOG_ACTIONS = [
  AUDIT_LOG_ACTION_INSERT,
  AUDIT_LOG_ACTION_UPDATE,
  AUDIT_LOG_ACTION_DELETE,
];

export interface IAuditLogEntry {
  id: number;
  table_name: string;
  action: string;
  // a user "update" to DELETED USER, listed as a delete
  soft_delete: boolean;
  record_id: number | null;
  old_data: { [key: string]: any } | null;
  new_data: { [key: string]: any } | null;
  user_id: number | null;
  changed_by_name: string | null;
  changed_by_email: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}

export interface IAuditLogAuthor {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface IAuditLogDailyCount {
  date: string;
  count: number;
}

export interface IAuditLogChart {
  table_name: string;
  action: string;
  days: number;
  series: IAuditLogDailyCount[];
}

export interface IAuditLogTopEditor {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  count: number;
}

export interface IAuditLogDailyCharts {
  registered_users: IAuditLogChart;
  deleted_users: IAuditLogChart;
  created_service_providers: IAuditLogChart;
}

export interface IAuditLogCharts extends IAuditLogDailyCharts {
  top_editors: {
    days: number;
    editors: IAuditLogTopEditor[];
  };
  // server time the served (possibly cached) numbers were computed, and how long they are cached
  generated_at: string;
  cache_minutes: number;
}

export interface IAuditLogPage {
  data: IAuditLogEntry[];
  total: number;
  page: number;
  per_page: number;
}

export interface IAuditLogListParams {
  page: number;
  perPage: number;
  table_name?: string | null;
  action?: string | null;
  record_id?: string | null;
  user_id?: string | null;
  ip?: string | null;
  search?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

const base = settings.endpoint + 'settings/audit-logs';

function unwrap(response: any): any {
  return response && response.data ? response.data.data : null;
}

// Blank filters are left out so the backend never validates an empty string.
function withoutBlanks(params: IAuditLogListParams): { [key: string]: any } {
  const query: { [key: string]: any } = {};

  Object.keys(params).forEach((key) => {
    const value = (params as any)[key];

    if (value !== null && value !== undefined && String(value).trim() !== '') {
      query[key] = typeof value === 'string' ? value.trim() : value;
    }
  });

  return query;
}

export const auditLogService = {
  list(params: IAuditLogListParams): Promise<IAuditLogPage> {
    return axios({ url: base, method: 'GET', params: withoutBlanks(params) })
      .then((response: any) => {
        const page = unwrap(response);

        if (page && Array.isArray(page.data)) {
          return {
            data: page.data,
            total: page.total || 0,
            page: page.page || 1,
            per_page: page.per_page || params.perPage,
          };
        }

        return { data: [], total: 0, page: 1, per_page: params.perPage };
      });
  },

  searchAuthors(search: string): Promise<IAuditLogAuthor[]> {
    return axios({ url: base + '/authors', method: 'GET', params: { search } })
      .then((response: any) => {
        const authors = unwrap(response);

        return Array.isArray(authors) ? authors : [];
      });
  },

  charts(): Promise<IAuditLogCharts | null> {
    return axios({ url: base + '/charts', method: 'GET' }).then((response: any) => unwrap(response));
  },

  // Recomputes the charts and replaces the cached copy.
  refreshCharts(): Promise<IAuditLogCharts | null> {
    return axios({ url: base + '/charts/refresh', method: 'POST' }).then((response: any) => unwrap(response));
  },

  tableNames(): Promise<string[]> {
    return axios({ url: base + '/tables', method: 'GET' })
      .then((response: any) => {
        const tables = unwrap(response);

        return Array.isArray(tables) ? tables : [];
      });
  },
};
